import React, { useEffect, useRef, useState } from "react";
import ChatMessage from "./components/ChatMessage";
// import AttachIcon from "./components/AttachIcon";
import SendIcon from "./components/SendIcon";
import { useClientChat } from "./hooks/useClientChat";
import chatBackgroundUrl from "../assets/chatbackground.png";
import styles from "./ClientChatPage.module.css";

export interface ClientChatPageProps {
  /** UUID клиента из маршрута /chat/:uuid */
  uuid: string;
}

const ClientChatPage: React.FC<ClientChatPageProps> = ({ uuid }) => {
  const {
    messages,
    messageText,
    setMessageText,
    isReady,
    isSending,
    isConnecting,
    error,
    sendMessage,
  } = useClientChat(uuid);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const initialScrollTimeoutRef = useRef<number | null>(null);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isInitialHistoryLoading, setIsInitialHistoryLoading] = useState(true);

  useEffect(() => {
    setIsInitialHistoryLoading(true);
    if (initialScrollTimeoutRef.current !== null) {
      window.clearTimeout(initialScrollTimeoutRef.current);
      initialScrollTimeoutRef.current = null;
    }
  }, [uuid]);

  useEffect(() => {
    if (messages.length === 0) return;

    if (isInitialHistoryLoading) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });

      if (initialScrollTimeoutRef.current !== null) {
        window.clearTimeout(initialScrollTimeoutRef.current);
      }

      // Wait briefly until batched history updates settle, then hide loader.
      initialScrollTimeoutRef.current = window.setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
        setIsInitialHistoryLoading(false);
        initialScrollTimeoutRef.current = null;
      }, 120);
      return;
    }

    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [isInitialHistoryLoading, messages.length]);

  useEffect(
    () => () => {
      if (initialScrollTimeoutRef.current !== null) {
        window.clearTimeout(initialScrollTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const textarea = messageInputRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";

    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = Number.parseFloat(computedStyle.lineHeight || "20") || 20;
    const paddingTop = Number.parseFloat(computedStyle.paddingTop || "0") || 0;
    const paddingBottom = Number.parseFloat(computedStyle.paddingBottom || "0") || 0;
    const maxHeight = Math.round(lineHeight * 3 + paddingTop + paddingBottom);

    const nextHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [messageText]);

  useEffect(() => {
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const previousViewportContent = viewportMeta?.getAttribute("content") ?? null;

    // In the mobile app webview chat should stay fixed in its viewport area.
    if (viewportMeta) {
      viewportMeta.setAttribute(
        "content",
        "width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover"
      );
    }

    let lastTouchEnd = 0;
    const preventGesture = (event: Event) => {
      event.preventDefault();
    };
    const preventPinchZoom = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };
    const preventDoubleTapZoom = (event: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    };

    document.addEventListener("gesturestart", preventGesture, { passive: false });
    document.addEventListener("gesturechange", preventGesture, { passive: false });
    document.addEventListener("gestureend", preventGesture, { passive: false });
    document.addEventListener("touchstart", preventPinchZoom, { passive: false });
    document.addEventListener("touchend", preventDoubleTapZoom, { passive: false });

    return () => {
      if (viewportMeta) {
        if (previousViewportContent !== null) {
          viewportMeta.setAttribute("content", previousViewportContent);
        } else {
          viewportMeta.removeAttribute("content");
        }
      }
      document.removeEventListener("gesturestart", preventGesture);
      document.removeEventListener("gesturechange", preventGesture);
      document.removeEventListener("gestureend", preventGesture);
      document.removeEventListener("touchstart", preventPinchZoom);
      document.removeEventListener("touchend", preventDoubleTapZoom);
    };
  }, []);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const updateKeyboardInset = () => {
      if (!isInputFocused) {
        setKeyboardInset(0);
        return;
      }

      const inset = Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
      setKeyboardInset(inset > 0 ? inset : 0);
    };

    updateKeyboardInset();
    viewport.addEventListener("resize", updateKeyboardInset);
    viewport.addEventListener("scroll", updateKeyboardInset);
    window.addEventListener("orientationchange", updateKeyboardInset);

    return () => {
      viewport.removeEventListener("resize", updateKeyboardInset);
      viewport.removeEventListener("scroll", updateKeyboardInset);
      window.removeEventListener("orientationchange", updateKeyboardInset);
    };
  }, [isInputFocused]);

  const canSend = isReady && !isSending && messageText.trim().length > 0;
  const showErrorOverlay = Boolean(error);
  const showCenteredOverlay =
    showErrorOverlay || (isConnecting && messages.length === 0);
  const showEmptyState = messages.length === 0 && !isConnecting && !error;
  const shouldRenderMessages = !showErrorOverlay;
  const showInitialLoader = messages.length > 0 && isInitialHistoryLoading;

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    sendMessage();
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isTouchLikeDevice =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;

    // On mobile keyboards Enter should create a new line.
    if (isTouchLikeDevice) return;

    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (canSend) sendMessage();
    }
  };

  return (
    <div className={styles.page}>
      <main className={styles.main}>
        <div
          className={styles.messagesArea}
          style={{ backgroundImage: `url(${chatBackgroundUrl})` }}
        >
          <div className={styles.messagesScroll}>
            {shouldRenderMessages
              ? messages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    text={message.text}
                    sender={message.sender}
                    timeLabel={message.timeLabel}
                    isOutgoing={message.isOutgoing}
                  />
                ))
              : null}
            <div ref={messagesEndRef} />
          </div>

          {showCenteredOverlay ? (
            <div className={styles.centerOverlay}>
              {error ? (
                <p className={styles.serviceNotice}>{error}</p>
              ) : isConnecting ? (
                <p className={styles.state}>Подключение…</p>
              ) : null}
            </div>
          ) : null}

          {showEmptyState ? (
            <div className={styles.centerOverlay}>
              <p className={styles.state}>Сообщений пока нет</p>
            </div>
          ) : null}

          {showInitialLoader ? (
            <div className={styles.centerOverlay}>
              <div className={styles.loadingBox}>
                <span className={styles.loadingSpinner} aria-hidden="true" />
                <span className={styles.loadingText}>Загрузка сообщений…</span>
              </div>
            </div>
          ) : null}
        </div>

        <form
          className={styles.sendForm}
          onSubmit={onSubmit}
          style={keyboardInset > 0 ? { marginBottom: `${keyboardInset}px` } : undefined}
        >
          <div className={styles.sendPrimaryRow}>
            {/* <button
              type="button"
              className={styles.attachButton}
              disabled={!isReady}
              aria-label="Прикрепить файл"
              title="Прикрепить файл"
            >
              <AttachIcon />
            </button> */}
            <textarea
              ref={messageInputRef}
              className={styles.sendInput}
              value={messageText}
              onChange={(event) => setMessageText(event.target.value)}
              onKeyDown={onInputKeyDown}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => {
                setIsInputFocused(false);
                setKeyboardInset(0);
              }}
              placeholder="Текст сообщения"
              disabled={!isReady || isSending}
              rows={1}
            />
            <button
              type="submit"
              className={styles.sendButton}
              disabled={!canSend}
              aria-label="Отправить"
              title="Отправить"
            >
              <SendIcon />
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ClientChatPage;
